import React, { useEffect, useState, useCallback, useRef } from "react";
import { DownloadCloud, Check } from "./../../Public/assets";
import { CustomDatePicker } from "./../../Components/DatePicker";
import { useOverview } from "./../../hooks";
import Table from "./../../Components/Table";
import {
  getDownloadCsv,
  getDownloadTransaction,
  TransactionDetails,
} from "./../../api";
import {
  formatCurrency,
  formatDateToShort,
  formatDateToYMD,
  formatChargePurpose,
  getParentTransactionNo,
  getFeeLabel,
  getIcon,
} from "./../../Utils/helperFunction";
import { Clock, X, CreditCard } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./../../Components/ui/select";
import Loader from "./../../Components/Loader/PageLoader";
import Footer from "./../../Components/Footer";
import { STATUS_MESSAGES, CURRENCY_OPTIONS } from "./../../Utils/Contant";

// Transaction nature mapping - determines if amount should show in debit or credit column
const TRANSACTION_NATURE = {
  FUND_RECEIVED: "CREDIT",
  REFUND: "CREDIT",
  REVERSAL: "CREDIT",
  CLEARING: "DEBIT",

  FEES: "DEBIT",
  FUND_TRANSFER: "DEBIT",
  TRANSFER: "DEBIT",
  CHARGE: "DEBIT",
  AUTHORIZATION: "DEBIT",

  CONVERSION: "BOTH",
};

// Helper components
const StatusTag = ({ status }) => (
  <span className="flex items-center w-fit gap-2 px-1.5 py-0.5 rounded border font-geomanistRegular text-[12px]">
    {status === "PAID" ||
    status === "FUNDED" ||
    status === "FULLY_CLEARED" ||
    status === "REVERSAL" ||
    status === "PARTIALLY_REVERSED" ||
    status === "COMPLETED" ||
    status === "FULLY_REVERSED" ||
    status === "PARTIALLY_CLEARED" ? (
      <img src={Check} alt="" className="w-3 h-3" />
    ) : status === "PROCESSING" || status === "SENT" || status === "PENDING" ? (
      <Clock className="w-3 h-3 text-orange-500" />
    ) : (
      <X className="w-3 h-3 text-red-500" />
    )}
    {STATUS_MESSAGES[status] || " "}
  </span>
);

const getTransactionTitle = (transaction, fallback) => {
  switch (transaction?.transaction_type) {
    case "FUND_RECEIVED":
      return transaction?.transaction_from_account_desc;
    case "FEES":
      return getFeeLabel(transaction?.transaction_remarks);
    case "CHARGE":
      return (
        formatChargePurpose(transaction?.transaction_purpose) ||
        transaction?.transaction_to_account_desc ||
        fallback
      );
    default:
      return transaction?.transaction_to_account_desc || fallback;
  }
};

const TransactionCard = ({
  transaction,
  formatDateToIST,
  transactionCurrencyFilter,
  transactions,
}) => {
  const parentTransactionNo = getParentTransactionNo(transaction, transactions);

  return (
    <div
      key={transaction.transaction_transaction_no}
      className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200"
    >
      <div className="flex items-start gap-3 w-full">
        <div className="min-w-10 h-10 bg-gray-50 rounded-full flex items-center justify-center">
          {getIcon({ type: transaction.transaction_type })}
        </div>
        <div className="flex flex-row justify-between w-[90%] h-[100%]">
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-900">
                {getTransactionTitle(transaction)}
              </span>
            </div>

            <span className="text-xs text-[#475467]">
              {parentTransactionNo
                ? parentTransactionNo
                : transaction.transaction_type === "CONVERSION"
                ? `${transaction.transaction_from_currency} to ${transaction.transaction_to_currency}`
                : transaction.transaction_type === "CARD_SPEND"
                  ? "Debit Card"
                  : transaction.transaction_type === "CLEARING" ||
                      transaction.transaction_type === "PENDING" ||
                      transaction.transaction_type === "AUTHORIZATION" ||
                      transaction.transaction_type === "REVERSAL"
                    ? transaction.transaction_type
                        .toLowerCase()
                        .replace(/[_-]+/g, " ")
                        .replace(/\b\w/g, (ch) => ch.toUpperCase())
                    : transaction.transaction_transfer_method === "LOCAL"
                      ? "Borderless Instant"
                      : transaction.transaction_transfer_method}
            </span>
            <span className="text-xs text-[#475467]">
              {transaction.transaction_transaction_no}
            </span>
            <span className="text-xs text-gray-700">
              {transaction.transaction_type === "CONVERSION" &&
              transactionCurrencyFilter !== "All" &&
              transactionCurrencyFilter ===
                transaction.transaction_from_currency
                ? formatCurrency(transaction.transaction_debit_amount)
                : formatCurrency(transaction.transaction_amount)}{" "}
              {transaction.transaction_type === "CONVERSION" &&
              transactionCurrencyFilter !== "All" &&
              transactionCurrencyFilter ===
                transaction.transaction_from_currency
                ? transaction.transaction_from_currency
                : transaction.transaction_to_currency}
            </span>
          </div>
          <div className="flex flex-col justify-between items-end">
            <StatusTag status={transaction.transaction_status} />
            <div className="space-x-1">
              <span className="text-xs font-geomanistRegular">
                {formatDateToShort(transaction.transaction_created_at)}
              </span>
              <span className="text-xs text-[#475467]">
                {formatDateToIST(transaction.transaction_created_at)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function TransactionOverView() {
  const {
    getFlagByCurrencyCode,
    transactionCurrencyFilter,
    setTransactionCurrencyFilter,
  } = useOverview();

  // Date setup
  const today = new Date();
  const oneMonthAgo = new Date(today);
  oneMonthAgo.setDate(today.getDate() - 30);

  // Date formatting
  const formatDate = useCallback((date) => {
    if (!date) return "";
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = months[date.getMonth()];
    const day = date.getDate().toString().padStart(2, "0");
    const year = date.getFullYear().toString()?.slice(-2);
    return `${month} ${day}, ${year}`;
  }, []);

  const formatDateToIST = useCallback((dateString) => {
    const date = new Date(dateString);
    const options = {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
      timeZone: "Asia/Kolkata",
    };
    return `${new Intl.DateTimeFormat("en-GB", options).format(date)} IST`;
  }, []);

  // State
  const [fromDate, setFromDate] = useState(formatDate(oneMonthAgo));
  const [toDate, setToDate] = useState(formatDate(today));
  const [displayCalendar, setDisplayCalendar] = useState(false);
  const [displayToCalendar, setDisplayToCalendar] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [transactionData, setTransactionData] = useState({
    transactions: [],
    hasNext: false,
    count: 0,
  });
  const [nextPageTransactions, setNextPageTransactions] = useState([]);
  const [mobileTransactions, setMobileTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedValue, setSelectedValue] = useState("all");
  const [isSuccess, setIsSuccess] = useState(false);
  const [singleRowSuccess, setSingleRowSuccess] = useState({});
  const [hasMore, setHasMore] = useState(true);
  const [mobilePage, setMobilePage] = useState(1);
  const observerRef = useRef(null);
  const lastElementRef = useRef(null);

  // Helpers
  const getStatusMessage = useCallback((status) => {
    return STATUS_MESSAGES[status] || " ";
  }, []);

  const hideCalendar = useCallback(() => {
    setDisplayToCalendar(false);
    setDisplayCalendar(false);
  }, []);

  const handleSelect = useCallback(
    (date, setDate) => {
      setDate(formatDate(date));
      hideCalendar();
    },
    [formatDate, hideCalendar],
  );

  const handleCurrencyChange = useCallback(
    (currency) => {
      setTransactionCurrencyFilter(currency);
    },
    [setTransactionCurrencyFilter],
  );

  // Download handlers
  const handleDownload = useCallback(async (transactionNo, transactionId) => {
    setSingleRowSuccess((prev) => ({
      ...prev,
      [transactionId]: { loading: true, success: false },
    }));

    await getDownloadTransaction(transactionNo, setSingleRowSuccess);

    setSingleRowSuccess((prev) => ({
      ...prev,
      [transactionId]: { loading: false, success: true },
    }));
  }, []);

  const handleDownloadCsv = useCallback(async () => {
    setIsSuccess({ loading: true, success: false });
    await getDownloadCsv({
      toDate,
      fromDate,
      status: selectedValue !== "all" ? selectedValue : "",
      currency:
        transactionCurrencyFilter !== "All" &&
        transactionCurrencyFilter !== "Debit Card"
          ? transactionCurrencyFilter
          : "",
      type: transactionCurrencyFilter === "Debit Card" ? "card" : "",
      setIsSuccess,
    });
    setIsSuccess({ loading: false, success: true });
  }, [toDate, fromDate, selectedValue, transactionCurrencyFilter]);

  // Data fetching for desktop
  const resetPageAndFetchTransactions = useCallback(() => {
    setCurrentPage(1);
  }, []);

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setTransactionData({ transactions: [], hasNext: false, count: 0 });
      setNextPageTransactions([]);

      const params = {
        pageSize: 5,
        toDate: toDate && formatDateToYMD(toDate),
        fromDate: fromDate && formatDateToYMD(fromDate),
        status: selectedValue !== "all" ? selectedValue : "",
        currency:
          transactionCurrencyFilter !== "All" &&
          transactionCurrencyFilter !== "Debit Card"
            ? transactionCurrencyFilter
            : "",
        type: transactionCurrencyFilter === "Debit Card" ? "card" : "",
      };

      const [response, nextResponse] = await Promise.all([
        TransactionDetails({ ...params, page: currentPage }),
        TransactionDetails({ ...params, page: currentPage + 1 }).catch(
          () => null,
        ),
      ]);

      const filteredData = response?.data?.transactions || response?.data || [];

      // Preserve the response structure with pagination data
      const dataToSet = {
        transactions: filteredData,
        hasNext: response?.data?.hasNext,
        count: response?.data?.count,
      };

      setTransactionData(dataToSet);
      setNextPageTransactions(
        response?.data?.hasNext ? nextResponse?.data?.transactions || [] : [],
      );
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, toDate, fromDate, selectedValue, transactionCurrencyFilter]);

  // Mobile infinite scroll data fetching
  const fetchMobileTransactions = useCallback(
    async (page, isInitial = false) => {
      try {
        if (isInitial) {
          setLoading(true);
          setMobileTransactions([]);
        } else {
          setLoadingMore(true);
        }

        const response = await TransactionDetails({
          page: page,
          pageSize: 10,
          toDate: toDate && formatDateToYMD(toDate),
          fromDate: fromDate && formatDateToYMD(fromDate),
          status: selectedValue !== "all" ? selectedValue : "",
          currency:
            transactionCurrencyFilter !== "All" &&
            transactionCurrencyFilter !== "Debit Card"
              ? transactionCurrencyFilter
              : "",
          type: transactionCurrencyFilter === "Debit Card" ? "card" : "",
        });

        const transactions = response?.data?.transactions || [];

        if (isInitial) {
          setMobileTransactions(transactions);
        } else {
          if (transactions && transactions.length > 0) {
            setMobileTransactions((prevTransactions) => [
              ...prevTransactions,
              ...transactions,
            ]);
          } else {
            setHasMore(false);
          }
        }
      } catch (error) {
        console.error("Error fetching mobile transactions:", error);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [toDate, fromDate, selectedValue, transactionCurrencyFilter],
  );

  // Reset and load initial mobile data when filters change
  useEffect(() => {
    setMobilePage(1);
    setHasMore(true);
    fetchMobileTransactions(1, true);
  }, [
    toDate,
    fromDate,
    selectedValue,
    transactionCurrencyFilter,
    fetchMobileTransactions,
  ]);

  // Desktop data fetching
  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  useEffect(() => {
    resetPageAndFetchTransactions();
  }, [
    toDate,
    fromDate,
    selectedValue,
    transactionCurrencyFilter,
    resetPageAndFetchTransactions,
  ]);

  // Intersection Observer for infinite scrolling
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: "20px",
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loadingMore && !loading) {
        setMobilePage((prevPage) => prevPage + 1);
      }
    }, options);

    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loadingMore, loading]);

  // Attach observer to last element
  useEffect(() => {
    if (lastElementRef.current && observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current.observe(lastElementRef.current);
    }
  }, [mobileTransactions]);

  // Load more data when mobilePage changes
  useEffect(() => {
    if (mobilePage > 1) {
      fetchMobileTransactions(mobilePage);
    }
  }, [mobilePage, fetchMobileTransactions]);

  // Table configuration
  const columns = [
    {
      id: "to_account_user_name",
      label: "Sent to",
      width: "max-w-[180px]",
      render: (sentTo, row) => {
        // const isConversion =
        //   row.transaction_type === "CONVERSION" &&
        //   transactionCurrencyFilter !== "All";
        // const DisplayIcon =
        //   transactionCurrencyFilter === row.transaction_from_currency
        //     ? "TRANSFER"
        //     : "FUND_RECEIVED";

        // Determine the display text
        const displayText = getTransactionTitle(row, sentTo);

        function toTitleWithSpaces(str) {
          if (!str || typeof str !== "string") {
            return "";
          }

          return str
            .toLowerCase()
            .replace(/[_-]+/g, " ")
            .replace(/\b\w/g, (ch) => ch.toUpperCase());
        }

        const parentTransactionNo = getParentTransactionNo(row, [
          ...(transactionData?.transactions || []),
          ...nextPageTransactions,
        ]);

        const subText = parentTransactionNo
          ? parentTransactionNo
          : row?.transaction_type === "CONVERSION"
            ? `${row?.transaction_from_currency} to ${row?.transaction_to_currency}`
            : row?.transaction_type === "CARD_SPEND"
              ? "Debit Card"
              : row?.transaction_type === "CLEARING" ||
                  row?.transaction_type === "PENDING" ||
                  row?.transaction_type === "AUTHORIZATION" ||
                  row?.transaction_type === "REVERSAL" ||
                  row?.transaction_type === "REFUND"
                ? toTitleWithSpaces(row?.transaction_type)
                : row.transaction_transfer_method === "LOCAL"
                  ? "Borderless Instant"
                  : row.transaction_transfer_method;

        return (
          <div className="flex items-center gap-2">
            <div className="flex justify-center items-center w-[35px] h-[35px] rounded-full bg-greyish">
              {getIcon({
                // type: isConversion ? DisplayIcon : row.transaction_type,
                type: row.transaction_type,
              })}
            </div>
            <div className="min-w-0 overflow-hidden">
              {/* Updated by Vikas */}
              <div
                className="font-geomanistBold text-[13px] leading-[18px] font-normal text-gray-900 truncate"
                title={displayText}
              >
                {displayText}
              </div>
              <div
                className="font-geomanistRegular text-[13px] leading-[18px] font-normal text-gray-500 truncate"
                title={subText}
              >
                {subText}
              </div>
            </div>
          </div>
        );
      },
    },

    {
      id: "transaction_transaction_no",
      label: "Transaction ID",
      width: "max-w-[150px]",
    },
    {
      id: "transaction_amount",
      label: "Amount Debited",
      width: "max-w-[120px]",
      render: (amount, row) => {
        const transactionNature = TRANSACTION_NATURE[row?.transaction_type];

        // Only show debit amount for DEBIT or BOTH transaction types
        if (transactionNature !== "DEBIT" && transactionNature !== "BOTH") {
          return "-";
        }

        // Use transaction_debit_amount if available, otherwise fall back to transaction_amount
        // const debitAmount = Number(
        //   row?.transaction_debit_amount ?? row?.transaction_amount
        // );
        
        const isConversion = row.transaction_type === "CONVERSION";
        const debitAmount =
          isConversion &&
          transactionCurrencyFilter !== "All" &&
          transactionCurrencyFilter !== row.transaction_from_currency
            ? "_"
            : // ? row.transaction_debit_amount
              row.transaction_amount;

              
        if (!Number.isFinite(debitAmount) || debitAmount === 0) {
          return "-";
        }

        return (
          <div className="flex gap-1">
            <div className="font-geomanistRegular text-[14px] leading-[18px] font-normal text-[#475467]">
              {formatCurrency(
                row?.transaction_debit_amount ?? row?.transaction_amount,
              )}
            </div>
            <div className="font-geomanistRegular text-[14px] leading-[18px] font-normal text-[#475467]">
              {row?.transaction_from_currency}
            </div>
          </div>
        );
      },
    },
    {
      id: "transaction_amount",
      label: "Amount Credited",
      width: "max-w-[120px]",
      render: (amount, row) => {
        const transactionNature = TRANSACTION_NATURE[row?.transaction_type];

        // Only show credit amount for CREDIT or BOTH transaction types
        if (transactionNature !== "CREDIT" && transactionNature !== "BOTH") {
          return "-";
        }

        const isConversion = row.transaction_type === "CONVERSION";
        const displayAmount =
          isConversion &&
          transactionCurrencyFilter !== "All" &&
          transactionCurrencyFilter === row.transaction_from_currency
            ? "_"
            : // ? row.transaction_debit_amount
              row.transaction_amount;

        const displayCurrency =
          isConversion &&
          transactionCurrencyFilter !== "All" &&
          transactionCurrencyFilter === row.transaction_from_currency
            ? "_"
            : // ? row.transaction_from_currency
              row.transaction_to_currency;

        if (!Number.isFinite(displayAmount) || displayAmount === 0) {
          return "-";
        }

        return (
          <div className="flex gap-1">
            <div className="font-geomanistRegular text-[14px] leading-[18px] font-normal text-[#475467]">
              {formatCurrency(displayAmount)}
            </div>
            <div className="font-geomanistRegular text-[14px] leading-[18px] font-normal text-[#475467]">
              {displayCurrency}
            </div>
          </div>
        );
      },
    },
    {
      id: "transaction_created_at",
      label: "Date & Time",
      width: "max-w-[150px]",
      render: (date) => (
        <div className="flex flex-col justify-center">
          <div className="font-geomanistBold text-[14px] leading-[18px] font-normal text-[#475467]">
            {formatDateToShort(date)}
          </div>
          <div className="font-geomanistRegular text-[11px] leading-[16px] font-normal text-[#475467]">
            {formatDateToIST(date)}
          </div>
        </div>
      ),
    },
    {
      id: "transaction_status",
      label: "Status",
      width: "max-w-[120px]",
      render: (status) => (
        <div className="w-fit px-1 flex items-center border border-[#D0D5DD] border-borderTable rounded-sm">
          <span className="rounded-full mr-1">
            {status === "PAID" ||
            status === "FUNDED" ||
            status === "FULLY_CLEARED" ||
            status === "REVERSAL" ||
            status === "PARTIALLY_REVERSED" ||
            status === "FULLY_REVERSED" ||
            status === "COMPLETED" ||
            status === "PARTIALLY_CLEARED" ? (
              <img src={Check} alt="" className="w-3 h-3 text-green-500" />
            ) : status === "PROCESSING" ||
              status === "SENT" ||
              status === "PENDING" ? (
              <Clock className="w-3 h-3 text-orange-500" />
            ) : (
              <X className="w-3 h-3 text-red-500" />
            )}
          </span>
          <span className="font-geomanistBold text-[#344054] text-[14px] leading-[18px] p-1">
            {getStatusMessage(status)}
          </span>
        </div>
      ),
    },
    {
      id: "actions",
      width: "max-w-[10px]",
      render: (_, row) => {
        if (row?.transaction_type === "CHARGE") return null;
        if (row?.transaction_type === "REFUND") return null;
        if (row?.transaction_type === "FEES") return null;
        if (row?.transaction_type === "CARD_LOAD") return null;
        if (row?.transaction_type === "CARD_SPEND") return null;
        if (row?.transaction_type === "CLEARING") return null;
        if (row?.transaction_type === "AUTHORIZATION") return null;
        if (row?.transaction_type === "REVERSAL") return null;

        if (row?.transaction_status !== "PAID") return null;

        const { loading, success } =
          singleRowSuccess[row?.transaction_transaction_id] || {};

        return (
          <div className="flex items-center justify-center">
            {loading ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : success ? (
              <img src={Check} alt="Success" className="h-5 w-5" />
            ) : (
              <img
                src={DownloadCloud}
                title="Download transaction receipt"
                alt="More options"
                className="w-4 h-4 cursor-pointer"
                onClick={() =>
                  handleDownload(
                    row?.transaction_transaction_no,
                    row?.transaction_transaction_id,
                  )
                }
              />
            )}
          </div>
        );
      },
    },
  ];

  // Render
  return (
    <div className="flex flex-col min-h-[calc(100dvh-114px)] lg:min-h-[calc(100dvh-146px)]">
      <div className="flex-grow overflow-auto">
        {/* Header */}
        <div className="bg-white px-6 xl:px-40 pt-4 lg:pt-10">
          <h1 className="text-xl text-greyish font-geomanistBold mb-4 leading-[28px]">
            Transactions
          </h1>
        </div>

        {/* Date Filters and Download Button */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 px-6 xl:px-40">
          <div className="flex items-center justify-between md:justify-normal md:space-x-2">
            <div>
              <CustomDatePicker
                date={fromDate}
                text="From"
                onChange={(date) => handleSelect(date, setFromDate)}
                maxDate={new Date()}
                displayCalendar={displayCalendar}
                setDisplayCalendar={setDisplayCalendar}
              />
            </div>
            <div>
              <CustomDatePicker
                date={toDate}
                text="To"
                onChange={(date) => handleSelect(date, setToDate)}
                minDate={fromDate}
                maxDate={new Date()}
                setDisplayCalendar={setDisplayToCalendar}
                displayCalendar={displayToCalendar}
              />
            </div>
          </div>

          {/* Download CSV Button */}
          <div
            className={`hidden md:flex gap-2 items-center border px-4 py-2 text-[14px] leading-[20px] rounded-lg transition-colors duration-300 cursor-pointer ${
              isSuccess?.success
                ? "border-green-500 bg-green-100 text-green-600"
                : "border-grey"
            }`}
            onClick={handleDownloadCsv}
          >
            {isSuccess?.loading ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : isSuccess?.success ? (
              <img src={Check} alt="Download" className="h-5 w-5" />
            ) : (
              <img src={DownloadCloud} alt="Download" className="h-5 w-5" />
            )}
            Download
          </div>
        </div>

        {/* Desktop Currency and Status Filters */}
        <div className="hidden lg:flex flex-col space-y-[17px] mb-4 px-6 xl:px-40">
          <div className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-blueish" />
            <span className="font-geomanistBold text-blackShade text-[16px] leading-[16px]">
              Filter by Currency:
            </span>
          </div>
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              {CURRENCY_OPTIONS.map((currency) => (
                <button
                  key={currency}
                  className={`flex gap-2 justify-center items-center px-3 font-geomanistBold min-w-[90px] border border-grey py-1 rounded-md ${
                    transactionCurrencyFilter === currency
                      ? "bg-blueish text-white"
                      : "hover:bg-gray-200"
                  }`}
                  onClick={() => handleCurrencyChange(currency)}
                >
                  {currency === "Debit Card" ? (
                    <CreditCard className="w-4 h-4" />
                  ) : (
                    <img
                      src={getFlagByCurrencyCode(currency)}
                      className={
                        getFlagByCurrencyCode(currency) ? "" : "hidden"
                      }
                      alt=""
                      width={22}
                    />
                  )}
                  {currency}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Select value={selectedValue} onValueChange={setSelectedValue}>
                <SelectTrigger className="w-40 h-8 bg-gray-50 border border-grey rounded-md text-[14px] leading-[20px] text-[#767C8F] font-geomanistBold">
                  <SelectValue
                    defaultValue="all"
                    placeholder="All Transactions"
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Transactions</SelectItem>
                  <SelectItem value="FUNDED">Fund Received</SelectItem>
                  <SelectItem value="PAID">Successful</SelectItem>
                  <SelectItem value="PROCESSING">Initiated</SelectItem>
                  <SelectItem value="SENT">Processing</SelectItem>
                  <SelectItem value="REJECTED">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Mobile Filters */}
        <div className="flex lg:hidden flex-col space-y-[17px] mb-4 px-6 xl:px-40">
          <div className="flex items-center justify-between md:space-x-2">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <CreditCard className="w-4 h-4 text-blueish" />
                <div className="font-geomanistBold text-blackShade text-[16px] leading-[16px]">
                  Filter by Currency
                </div>
              </div>
              <div className="flex justify-between items-center">
                <Select
                  value={transactionCurrencyFilter}
                  onValueChange={setTransactionCurrencyFilter}
                >
                  <SelectTrigger className="w-40 h-8 bg-gray-50 border border-grey rounded-md text-[14px] leading-[20px] text-[#767C8F] font-geomanistBold">
                    <SelectValue
                      defaultValue="All"
                      placeholder="All Currency"
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCY_OPTIONS.map((currency) => (
                      <SelectItem key={currency} value={currency}>
                        {currency}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <div className="font-geomanistBold text-blackShade text-[16px] mb-2 leading-[16px]">
                Filter by Transaction
              </div>
              <div className="flex justify-between items-center">
                <Select value={selectedValue} onValueChange={setSelectedValue}>
                  <SelectTrigger className="w-40 h-8 bg-gray-50 border border-grey rounded-md text-[14px] leading-[20px] text-[#767C8F] font-geomanistBold">
                    <SelectValue
                      defaultValue="all"
                      placeholder="All Transactions"
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Transactions</SelectItem>
                    <SelectItem value="FUNDED">Fund Received</SelectItem>
                    <SelectItem value="PAID">Successful</SelectItem>
                    <SelectItem value="PROCESSING">Initiated</SelectItem>
                    <SelectItem value="SENT">Processing</SelectItem>
                    <SelectItem value="REJECTED">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        {/* Transaction Data */}
        <div className="px-6 xl:px-40 pb-8">
          {loading ? (
            <div className="h-[300px] flex items-center justify-center">
              <Loader className="mr-2 w-20 h-20" />
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden lg:block w-full">
                <Table
                  data={transactionData}
                  columns={columns}
                  rowsPerPage={5}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                />
              </div>

              {/* Mobile Card View with Infinite Scrolling */}
              <div className="flex flex-col space-y-4 lg:hidden">
                {mobileTransactions.length > 0 ? (
                  <>
                    {mobileTransactions.map((transaction, index) => {
                      if (index === mobileTransactions.length - 1) {
                        return (
                          <div
                            key={
                              transaction.transaction_transaction_no ||
                              transaction.id
                            }
                            ref={lastElementRef}
                          >
                            <TransactionCard
                              transactionCurrencyFilter={
                                transactionCurrencyFilter
                              }
                              transaction={transaction}
                              transactions={mobileTransactions}
                              formatDateToIST={formatDateToIST}
                              getStatusMessage={getStatusMessage}
                            />
                          </div>
                        );
                      }
                      return (
                        <TransactionCard
                          key={
                            transaction.transaction_transaction_no ||
                            transaction.id
                          }
                          transactionCurrencyFilter={transactionCurrencyFilter}
                          transaction={transaction}
                          transactions={mobileTransactions}
                          formatDateToIST={formatDateToIST}
                          getStatusMessage={getStatusMessage}
                        />
                      );
                    })}
                    {/* Loading indicator for infinite scroll */}
                    {loadingMore && hasMore && (
                      <div className="flex justify-center py-4">
                        <Loader className="w-8 h-8 animate-spin" />
                      </div>
                    )}
                    {/* No more transactions message */}
                    {
                      !hasMore && mobileTransactions.length > 10 && ""
                      // <div className="text-center py-4 text-gray-500">
                      //   No more transactions to load
                      // </div>
                    }
                  </>
                ) : (
                  <div className="flex items-center justify-center p-4 bg-white rounded-lg border border-gray-200">
                    <span className="text-sm font-medium text-gray-500">
                      No transaction found
                    </span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Footer - Always at the bottom */}
      <div className="mt-auto">
        <Footer />
      </div>
    </div>
  );
}
export default TransactionOverView;
